import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandHoldingHeart, faPlus, faEdit, faTrash, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { adminDonationsAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const DonationManagement = () => {
  const [causes, setCauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedCause, setSelectedCause] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    goal_amount: '',
    is_active: true
  });

  useEffect(() => {
    fetchCauses();
  }, []);

  const fetchCauses = async () => {
    setLoading(true);
    try {
      const response = await adminDonationsAPI.getAllCauses();
      setCauses(response.data.causes);
    } catch (error) {
      toast.error('Failed to fetch donation causes');
      console.error('Fetch causes error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      name: '',
      description: '',
      goal_amount: '',
      is_active: true
    });
    setShowModal(true);
  };

  const handleEdit = (cause) => {
    setModalMode('edit');
    setSelectedCause(cause);
    setFormData({
      name: cause.name,
      description: cause.description || '',
      goal_amount: cause.goal_amount || '',
      is_active: cause.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        goal_amount: parseFloat(formData.goal_amount) || 0
      };

      if (modalMode === 'create') {
        await adminDonationsAPI.createCause(data);
        toast.success('Donation cause created successfully');
      } else {
        await adminDonationsAPI.updateCause(selectedCause.id, data);
        toast.success('Donation cause updated successfully');
      }

      setShowModal(false);
      fetchCauses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (cause) => {
    if (!window.confirm(`Are you sure you want to delete donation cause "${cause.name}"?`)) {
      return;
    }

    try {
      await adminDonationsAPI.deleteCause(cause.id);
      toast.success('Donation cause deleted successfully');
      fetchCauses();
    } catch (error) {
      toast.error('Failed to delete donation cause');
    }
  };

  const calculateProgress = (current, goal) => {
    if (!goal) return 0;
    return Math.min((current / goal) * 100, 100);
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={faHandHoldingHeart} />
          Donation Management
        </h1>
        <p>Manage donation causes and view donation statistics</p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Donation Causes ({causes.length})</h3>
          <button className="admin-btn-sm admin-btn-primary" onClick={handleCreate}>
            <FontAwesomeIcon icon={faPlus} />
            Create Cause
          </button>
        </div>

        {loading ? (
          <div className="admin-loading">
            <FontAwesomeIcon icon={faSpinner} spin />
            <p>Loading donation causes...</p>
          </div>
        ) : causes.length === 0 ? (
          <div className="admin-empty-state">
            <FontAwesomeIcon icon={faHandHoldingHeart} />
            <h3>No Donation Causes Found</h3>
            <p>Create your first donation cause to get started</p>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Goal</th>
                  <th>Raised</th>
                  <th>Progress</th>
                  <th>Donations</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {causes.map((cause) => (
                  <tr key={cause.id}>
                    <td data-label="ID">{cause.id}</td>
                    <td data-label="Name">{cause.name}</td>
                    <td data-label="Description" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cause.description || 'No description'}
                    </td>
                    <td data-label="Goal">₦{cause.goal_amount?.toLocaleString() || '0'}</td>
                    <td data-label="Raised">₦{cause.total_donated?.toLocaleString() || '0'}</td>
                    <td data-label="Progress">
                      <div style={{ width: '100px' }}>
                        <div style={{
                          background: 'var(--admin-border)',
                          height: '8px',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: 'var(--admin-success)',
                            height: '100%',
                            width: `${calculateProgress(cause.total_donated || 0, cause.goal_amount)}%`,
                            transition: 'width 0.3s'
                          }} />
                        </div>
                        <small style={{ color: 'var(--admin-text-muted)' }}>
                          {calculateProgress(cause.total_donated || 0, cause.goal_amount).toFixed(1)}%
                        </small>
                      </div>
                    </td>
                    <td data-label="Donations">{cause.donation_count || 0}</td>
                    <td data-label="Status">
                      <span className={`admin-badge ${cause.is_active ? 'success' : 'inactive'}`}>
                        {cause.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td data-label="Actions">
                      <div className="admin-actions">
                        <button
                          className="admin-btn-sm admin-btn-primary"
                          onClick={() => handleEdit(cause)}
                          title="Edit"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          className="admin-btn-sm admin-btn-danger"
                          onClick={() => handleDelete(cause)}
                          title="Delete"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {modalMode === 'create' ? 'Create New Donation Cause' : 'Edit Donation Cause'}
              </h2>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="admin-form-group">
                <label>Cause Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Enter cause name"
                />
              </div>

              <div className="admin-form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Enter cause description"
                  rows="4"
                />
              </div>

              <div className="admin-form-group">
                <label>Goal Amount (₦)</label>
                <input
                  type="number"
                  value={formData.goal_amount}
                  onChange={(e) => setFormData({...formData, goal_amount: e.target.value})}
                  min="0"
                  step="0.01"
                  placeholder="Enter goal amount"
                />
              </div>

              <div className="admin-form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    style={{ width: 'auto', marginRight: '0.5rem' }}
                  />
                  Active
                </label>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-btn-sm admin-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn-sm admin-btn-primary" disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : (modalMode === 'create' ? 'Create' : 'Update')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationManagement;
